<template>
	<div>
		<FormGroup
			label="Bitte teile uns mit, warum der Termin nicht stattgefunden hat"
			:label-classes="['font-weight-bold']"
		>
			<RadioGroup
				v-model="reason"
				:options="reasonOptions"
				stacked
			/>
		</FormGroup>
		<FormGroup
			v-if="reasonValid"
			label="Bitte wähle den Folgeschritt"
			class="mt-3"
			:label-classes="['font-weight-bold']"
		>
			<RadioGroup
				v-model="followUp"
				:options="followUpOptions"
				stacked
			/>
		</FormGroup>
		<JonInfo v-if="followUpAppointmentPossible" class="mt-3">
			<template #message>
				<p>{{ applicantName }} wird informiert, dass ein Termin möglich ist.</p>
				<p>
					Sobald {{ applicantName }} antwortet, erhältst du eine Benachrichtigung über die möglichen nächsten
					Schritte.
				</p>
			</template>
		</JonInfo>
		<CustomerSendOff
			v-if="followUpSendoff"
			v-model="sendOffData"
			:disclosed-data="message.disclosedData"
			:pseudonym="message.pseudonym"
			class="mt-3"
			:with-applicant-name="false"
		/>
		<div class="mt-3 text-right">
			<Button
				text="Senden"
				icon="checkmark"
				:disabled="!dataValid || sendingRequest"
				:loading="sendingRequest"
				@click="submit"
			/>
		</div>
	</div>
</template>

<script>
import Button from "@/vue/common/Button";
import FormGroup from "@/vue/common/form/Group"
import InfoBox from "@/vue/common/InfoBox";
import RadioGroup from "@/vue/common/form/RadioGroup";
import CustomerSendOff from "@/vue/communication/CustomerSendOff";
import {
	APPLICANT_CANCELLATION,
	APPLICANT_UNEXCUSED,
	CUSTOMER_CANCELLATION
} from "@/helper/communication/conversation/appointment/DidNotTakePlaceReason";
import {isDefined} from "@/helper/Value";
import Reply, {
	ACTION_ALTERNATIVE_APPOINTMENT_POSSIBLE,
	ACTION_SENDOFF
} from "@/api/communication/conversation/message/Reply";
import * as Toast from "@/helper/layout/Toast";
import flatten from "@/helper/error/Errors";
import JonInfo from "@/vue/common/JonInfo.vue";

const FOLLOW_UP_APPOINTMENT_POSSIBLE = 'appointment-possible';
const FOLLOW_UP_SENDOFF = 'sendoff';

export default {

	components: {
		JonInfo,
		Button,
		FormGroup,
		InfoBox,
		RadioGroup,
		CustomerSendOff,
	},

	props: {
		message: {
			type: Object,
			required: true,
		},
	},

	data ()
	{
		return {
			reason: undefined,
			followUp: undefined,
			sendOffData: undefined,
			sendingRequest: false,
		};
	},

	computed: {
		companyName ()
		{
			return this.message.customer.companyName;
		},
		applicantName ()
		{
			return this.message.disclosedData.name;
		},
		reasonOptions ()
		{
			return [
				{
					value: CUSTOMER_CANCELLATION,
					text: 'Weil Gesprächspartner von ' + this.companyName + ' den Termin abgesagt hat/haben'
				},
				{
					value: APPLICANT_UNEXCUSED,
					text: 'Weil ' + this.applicantName + ' ohne Absage nicht erschienen ist'
				},
				{
					value: APPLICANT_CANCELLATION,
					text: 'Weil ' + this.applicantName + ' den Termin abgesagt hat'
				},
			];
		},
		followUpOptions ()
		{
			return [
				{
					value: FOLLOW_UP_APPOINTMENT_POSSIBLE,
					text: 'Neuer Termin ist möglich - du bist nach wie vor interessiert'
				},
				{
					value: FOLLOW_UP_SENDOFF,
					text: 'Verabschieden von  ' + this.applicantName
				},
			];
		},
		reasonValid ()
		{
			return isDefined(this.reason);
		},
		followUpValid ()
		{
			return isDefined(this.followUp);
		},
		followUpAppointmentPossible ()
		{
			return this.followUp === FOLLOW_UP_APPOINTMENT_POSSIBLE;
		},
		followUpSendoff ()
		{
			return this.followUp === FOLLOW_UP_SENDOFF;
		},
		sendoffDataValid ()
		{
			return !this.followUpSendoff || isDefined(this.sendOffData);
		},
		dataValid ()
		{
			return this.reasonValid
				&& this.followUpValid
				&& this.sendoffDataValid;
		}
	},

	methods: {
		async submit ()
		{
			this.sendingRequest = true;

			try
			{
				let action = undefined;

				if (this.followUpSendoff)
				{
					action = ACTION_SENDOFF;
				}

				if (this.followUpAppointmentPossible)
				{
					action = ACTION_ALTERNATIVE_APPOINTMENT_POSSIBLE;
				}

				const reply = new Reply()
					.setMessageId(this.message.id)
					.setAction(action);

				reply.setAppointmentDidNotTakePlaceData({
					appointmentId: this.message.appointment.id,
					reason: this.reason
				});

				if (this.followUpSendoff)
				{
					reply.setCustomerSendOffData(this.sendOffData);
				}

				await reply.call();

				this.$emit('processed');
			}
			catch (e)
			{
				Toast.error(this, flatten(e.errors));
			}

			this.sendingRequest = false;
		}
	}
}
</script>
